/**
 * Pure mapper: assertions + sources + capped rules → evidence dashboard card (Task 20).
 * Winning assertion = highest source_confidence_bps, then latest verified_at (docs/02 §4).
 * No evidence source → field is unknown. Never invents values.
 */

import { asCents, type Cents } from '../../domain/money';
import {
  CARD_ASSERTION_WHITELIST,
  type CardAssertionField,
} from '../../domain/cardAssertionFields';
import { sourceConfidenceBps } from '../../domain/recommend/confidence';
import { formatBpsAsPercent, formatCentsDisplay } from '../../domain/recommend/explain';
import type { ConfidenceTier } from '../../domain/recommend/types';
import type {
  CapUsageView,
  EvidenceDashboardCardView,
  EvidenceFieldView,
  EvidenceSourceRef,
} from './types';

export type EvidenceAssertionInput = {
  id: string;
  entity_id: string;
  field_name: string;
  asserted_value: unknown;
  evidence_source_id: string;
  confidence: ConfidenceTier;
  verified_at: string;
  superseded_at: string | null;
  deleted_at: string | null;
};

export type EvidenceSourceInput = {
  id: string;
  kind: string;
  title: string;
};

export type CappedRuleInput = {
  id: string;
  category: string | null;
  spend_cap_cents: Cents;
  /** From local reward_cap_periods; null when no period row exists yet. */
  remaining_cap_cents: Cents | null;
};

export type BuildEvidenceCardInput = {
  card_id: string;
  label: string;
  assertions: readonly EvidenceAssertionInput[];
  sources: readonly EvidenceSourceInput[];
  capped_rules: readonly CappedRuleInput[];
};

function isLive(a: EvidenceAssertionInput): boolean {
  return a.superseded_at === null && a.deleted_at === null;
}

function compareVerifiedAt(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/** docs/02 §4 — highest confidence, then most recent verified_at, then id. */
export function selectWinningAssertion(
  assertions: readonly EvidenceAssertionInput[],
  cardId: string,
  fieldName: CardAssertionField,
): EvidenceAssertionInput | null {
  const live = assertions.filter(
    (a) => a.entity_id === cardId && a.field_name === fieldName && isLive(a),
  );
  if (live.length === 0) return null;

  let best = live[0];
  if (best === undefined) return null;

  for (let i = 1; i < live.length; i++) {
    const cand = live[i];
    if (cand === undefined) continue;
    const bestBps = sourceConfidenceBps(best.confidence);
    const candBps = sourceConfidenceBps(cand.confidence);
    if (candBps > bestBps) {
      best = cand;
      continue;
    }
    if (candBps < bestBps) continue;
    const dateCmp = compareVerifiedAt(cand.verified_at, best.verified_at);
    if (dateCmp > 0 || (dateCmp === 0 && cand.id > best.id)) {
      best = cand;
    }
  }
  return best;
}

function formatAssertedValue(field: CardAssertionField, value: unknown): string {
  if (value === null || value === undefined) return '—';

  if (field === 'annual_fee_cents' || field === 'monthly_fee_cents') {
    if (typeof value === 'bigint') return formatCentsDisplay(value);
    if (typeof value === 'number' && Number.isInteger(value)) {
      return formatCentsDisplay(BigInt(value));
    }
    if (typeof value === 'string' && /^-?\d+$/.test(value)) {
      return formatCentsDisplay(BigInt(value));
    }
    return JSON.stringify(value);
  }
  if (field === 'foreign_txn_fee_bps' || field === 'purchase_apr_bps') {
    if (typeof value === 'number' && Number.isInteger(value)) {
      return formatBpsAsPercent(value);
    }
    if (typeof value === 'string' && /^-?\d+$/.test(value)) {
      return formatBpsAsPercent(Number(value));
    }
    return JSON.stringify(value);
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return JSON.stringify(value);
}

function resolveSource(
  sources: readonly EvidenceSourceInput[],
  sourceId: string,
): EvidenceSourceRef | null {
  const src = sources.find((s) => s.id === sourceId);
  if (src === undefined) return null;
  return { id: src.id, kind: src.kind, title: src.title };
}

function buildField(
  cardId: string,
  fieldName: CardAssertionField,
  assertions: readonly EvidenceAssertionInput[],
  sources: readonly EvidenceSourceInput[],
): EvidenceFieldView {
  const winning = selectWinningAssertion(assertions, cardId, fieldName);
  if (winning === null) {
    return {
      field_name: fieldName,
      status: 'unknown',
      value_display: null,
      confidence: null,
      source: null,
      observed_at: null,
    };
  }

  const source = resolveSource(sources, winning.evidence_source_id);
  if (source === null) {
    // Assertion without a resolvable source cannot be shown as known.
    return {
      field_name: fieldName,
      status: 'unknown',
      value_display: null,
      confidence: null,
      source: null,
      observed_at: null,
    };
  }

  return {
    field_name: fieldName,
    status: 'known',
    value_display: formatAssertedValue(fieldName, winning.asserted_value),
    confidence: winning.confidence,
    source,
    observed_at: winning.verified_at,
  };
}

function buildCap(rule: CappedRuleInput): CapUsageView {
  const limit = rule.spend_cap_cents;
  const remaining = rule.remaining_cap_cents ?? limit;
  const used = remaining >= limit ? asCents(0n) : asCents(limit - remaining);
  const clampedRemaining = remaining < 0n ? asCents(0n) : remaining > limit ? limit : remaining;

  return {
    reward_rule_id: rule.id,
    label: rule.category ?? 'base',
    cap_limit_cents: limit,
    used_cents: used,
    remaining_cents: clampedRemaining,
  };
}

/**
 * Build one read-only evidence card. Missing assertions stay unknown.
 */
export function buildEvidenceDashboardCard(
  input: BuildEvidenceCardInput,
): EvidenceDashboardCardView {
  const fields = CARD_ASSERTION_WHITELIST.map((fieldName) =>
    buildField(input.card_id, fieldName, input.assertions, input.sources),
  );

  const caps = input.capped_rules.map(buildCap);

  return {
    card_id: input.card_id,
    label: input.label,
    fields,
    caps,
  };
}
